import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  AdminEmptyState,
  AdminErrorBanner,
  AdminPagination,
  AdminSearchBar,
} from './AdminTableChrome'
import {
  ADMIN_PAGE_SIZE,
  adminKeys,
  formatDateAr,
  type AdminQuiz,
  type AdminQuizListResponse,
} from './types'

/**
 * Renders a quiz's host/owner cell: display name (primary) over email
 * (secondary, `dir="ltr"`). Three cases distinguished by `createdByUserId` +
 * the resolved name: system/public quiz, a resolved owner, or an orphaned quiz
 * whose owner was deleted.
 */
function HostCell({ quiz }: { quiz: AdminQuiz }) {
  const { t } = useTranslation('admin')

  if (quiz.createdByUserId === null) {
    return <div className="text-brand-800">{t('quizHostSystem')}</div>
  }
  if (quiz.ownerDisplayName === null) {
    return <div className="text-brand-800">{t('quizHostUnknown')}</div>
  }
  return (
    <>
      <div className="text-brand-800">{quiz.ownerDisplayName}</div>
      {quiz.ownerEmail !== null ? (
        <div className="text-xs text-brand-600" dir="ltr">{quiz.ownerEmail}</div>
      ) : null}
    </>
  )
}

/**
 * Paginated, searchable cross-user quiz table (read-only oversight). Columns:
 * title (truncated with a tooltip), host (owner name over email), question
 * count, last updated. Renders a semantic `<table>` from `md` up and collapses
 * to stacked cards below it, matching the user table's conventions.
 */
export function AdminQuizTable() {
  const { t } = useTranslation('admin')
  const { t: tCommon } = useTranslation('common')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)

  useEffect(() => {
    const id = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(id)
  }, [searchInput])

  const quizzesQuery = useQuery({
    queryKey: adminKeys.quizzes({ search, page }),
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams()
      if (search) params.set('search', search)
      params.set('page', String(page))
      params.set('pageSize', String(ADMIN_PAGE_SIZE))
      const res = await fetch(`/api/v1/admin/quizzes?${params.toString()}`, {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load quizzes: ${res.status}`)
      }
      return (await res.json()) as AdminQuizListResponse
    },
  })

  const total = quizzesQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / ADMIN_PAGE_SIZE))
  const quizzes = quizzesQuery.data?.quizzes ?? []

  return (
    <div>
      <div className="mb-6">
        <AdminSearchBar
          value={searchInput}
          onChange={setSearchInput}
          placeholder={t('searchQuizzes')}
        />
      </div>

      {quizzesQuery.isLoading ? <TableSkeleton /> : null}

      {quizzesQuery.isError ? <AdminErrorBanner message={t('loadError')} /> : null}

      {quizzesQuery.isSuccess && quizzes.length === 0 ? (
        <AdminEmptyState message={search ? t('noResults') : t('noQuizzes')} />
      ) : null}

      {quizzesQuery.isSuccess && quizzes.length > 0 ? (
        <>
          {/* Desktop: semantic table. */}
          <div className="hidden overflow-hidden rounded-2xl border border-brand-100 bg-white md:block">
            <table className="w-full border-collapse text-sm" dir="rtl">
              <thead>
                <tr className="border-b border-brand-100 bg-brand-50/40 text-xs font-semibold uppercase tracking-wide text-brand-600">
                  <th scope="col" className="px-4 py-3 text-start">{t('quizColTitle')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('quizColHost')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('quizColQuestions')}</th>
                  <th scope="col" className="px-4 py-3 text-start">{t('quizColUpdated')}</th>
                </tr>
              </thead>
              <tbody>
                {quizzes.map((quiz) => (
                  <tr key={quiz.id} className="border-b border-brand-50 last:border-0 hover:bg-brand-50/30">
                    <td className="px-4 py-3 align-top max-w-xs">
                      <div className="truncate font-semibold text-brand-900" title={quiz.title}>
                        {quiz.title}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <HostCell quiz={quiz} />
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700 tabular-nums">
                      <span dir="ltr">{quiz.questionCount}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-brand-700">
                      <span dir="ltr">{formatDateAr(quiz.updatedAt)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: stacked label:value cards. */}
          <ul role="list" className="space-y-4 md:hidden">
            {quizzes.map((quiz) => (
              <li
                key={quiz.id}
                className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 ps-4 pe-4 py-4 shadow-sm"
              >
                <div className="font-semibold text-brand-900" title={quiz.title}>
                  {quiz.title}
                </div>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                  <dt className="text-brand-500">{t('quizColHost')}</dt>
                  <dd className="text-end"><HostCell quiz={quiz} /></dd>
                  <dt className="text-brand-500">{t('quizColQuestions')}</dt>
                  <dd className="text-brand-800 text-end tabular-nums" dir="ltr">{quiz.questionCount}</dd>
                  <dt className="text-brand-500">{t('quizColUpdated')}</dt>
                  <dd className="text-brand-800 text-end" dir="ltr">{formatDateAr(quiz.updatedAt)}</dd>
                </dl>
              </li>
            ))}
          </ul>

          <AdminPagination
            page={page}
            pageCount={pageCount}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => Math.min(pageCount, p + 1))}
          />
        </>
      ) : null}

      {quizzesQuery.isLoading ? (
        <span className="sr-only" role="status" aria-live="polite">
          {tCommon('loading')}
        </span>
      ) : null}
    </div>
  )
}

function TableSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-brand-100 bg-white" aria-hidden="true">
      <div className="space-y-px">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-4 animate-pulse">
            <div className="h-6 flex-1 rounded bg-brand-50" />
            <div className="h-6 w-24 rounded bg-brand-50" />
            <div className="h-6 w-12 rounded bg-brand-50" />
            <div className="h-6 w-24 rounded bg-brand-50" />
          </div>
        ))}
      </div>
    </div>
  )
}
